/**
 * LJ Upsell — Pre-checkout upsell modal
 * ======================================
 * Slide-up modal con productos de Joe Tech antes del checkout. Se dispara
 * al click en `[name="checkout"]` del cart drawer y permite al cliente
 * agregar productos extra antes de pagar.
 *
 * Comportamiento:
 *   - Una sola vez por sesión (sessionStorage flag)
 *   - Skip si el cart YA tiene un producto Joe Tech
 *   - Si JS falla, el form de checkout funciona normal (graceful degradation)
 *
 * Vistas internas:
 *   - grid: 8 productos en cards horizontales (mobile) / grid (desktop)
 *   - detail: producto seleccionado con prev/next y botón "Agregar"
 *
 * Después de agregar productos, el cart se actualiza vía /cart/add.js, el
 * cart drawer se refresca, y el footer del modal cambia a "Pagar S/X" con
 * el nuevo total para que el cliente vea el impacto de su decisión.
 */
(function () {
  'use strict';

  // ─── ELEMENTOS DEL DOM ────────────────────────────────────────────
  var modal = document.querySelector('[data-lj-upsell]');
  if (!modal) return;  // no hay coleccion joe-tech con productos

  var panel = modal.querySelector('.lj-upsell__panel');
  var cards = Array.from(modal.querySelectorAll('[data-lj-upsell-product]'));
  var viewGrid = modal.querySelector('[data-lj-upsell-view="grid"]');
  var viewDetail = modal.querySelector('[data-lj-upsell-view="detail"]');

  // Detail elements
  var detailContent = modal.querySelector('[data-lj-upsell-detail-content]');
  var detailSlides = modal.querySelector('[data-lj-upsell-detail-slides]');
  var detailGalleryDots = modal.querySelector('[data-lj-upsell-detail-gallery-dots]');
  var detailTitle = modal.querySelector('[data-lj-upsell-detail-title]');
  var detailDescription = modal.querySelector('[data-lj-upsell-detail-description]');
  var detailPrice = modal.querySelector('[data-lj-upsell-detail-price]');
  var detailDiscount = modal.querySelector('[data-lj-upsell-detail-discount]');
  var detailDiscountLabel = modal.querySelector('[data-lj-upsell-detail-discount-label]');
  var addBtn = modal.querySelector('[data-lj-upsell-add]');
  var addLabel = modal.querySelector('[data-lj-upsell-add-label]');
  var dotsContainer = modal.querySelector('[data-lj-upsell-dots]');

  // Countdown elements
  var countdownEl = modal.querySelector('[data-lj-upsell-countdown]');
  var countdownClock = modal.querySelector('[data-lj-upsell-countdown-clock]');

  // Footer
  var continueBtn = modal.querySelector('[data-lj-upsell-continue]');
  var continueLabel = modal.querySelector('[data-lj-upsell-continue-label]');

  // Toast (separate root)
  var toast = document.querySelector('[data-lj-upsell-toast]');
  var toastText = toast && toast.querySelector('[data-lj-upsell-toast-text]');

  // ─── STATE ────────────────────────────────────────────────────────
  var state = {
    isOpen: false,
    view: 'grid',
    currentIndex: 0,
    addedVariants: {},  // { variantId: true } — para evitar duplicar adds
    pendingCheckoutForm: null,   // form interceptado
    pendingCheckoutButton: null, // button[name=checkout] que disparó (necesario)
  };

  // SESSION_FLAG removido — el modal ahora se muestra cada vez que el cliente
  // clickea "Finalizar compra" (excepto si el cart ya tiene un producto Joe Tech).
  // El comportamiento "una sola vez" causaba confusion: parecia roto en el segundo
  // intento. Si el cliente re-intenta checkout, esta dando consentimiento implicito
  // a re-ver la oferta.
  // var SESSION_FLAG = 'lj_upsell_shown_v1';

  // ─── PORTAL: mover modal al body para escapar containing blocks ─────
  // Como el modal se incluye desde theme.liquid pero el cart drawer puede
  // estar dentro de un position: fixed, lo movemos al body para evitar
  // problemas de z-index y backdrop.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  if (toast && toast.parentElement !== document.body) {
    document.body.appendChild(toast);
  }

  // ─── HELPERS ──────────────────────────────────────────────────────
  function moneyFmt(cents) {
    return 'S/. ' + (cents / 100).toFixed(2);
  }

  function clearChildren(el) {
    // Safe alternative to el.innerHTML = '' — removes all children sin riesgo
    // de XSS warnings de tooling. Iteración mientras haya child.
    while (el && el.firstChild) el.firstChild.remove();
  }

  function cartHasJoeTechProduct() {
    return fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var upsellHandles = cards.map(function (c) { return c.dataset.handle; });
        return cart.items.some(function (item) {
          return upsellHandles.indexOf(item.handle) !== -1;
        });
      })
      .catch(function () { return false; });
  }

  // ─── DOTS (paginación visual) ────────────────────────────────────
  function buildDots() {
    if (!dotsContainer) return;
    clearChildren(dotsContainer);
    cards.forEach(function (_, i) {
      var dot = document.createElement('span');
      dot.className = 'lj-upsell-detail__dot';
      if (i === state.currentIndex) dot.classList.add('is-active');
      dotsContainer.appendChild(dot);
    });
  }

  // ─── GALLERY (slides + dots) ──────────────────────────────────────
  function buildGallery(card) {
    if (!detailSlides) return;
    clearChildren(detailSlides);
    if (detailGalleryDots) clearChildren(detailGalleryDots);

    // Parse el JSON array de imagenes; si falla, usa data-image como fallback
    var imageUrls = [];
    try {
      var rawImages = card.dataset.images;
      if (rawImages) imageUrls = JSON.parse(rawImages);
    } catch (e) { /* invalid JSON, fallback */ }
    if (imageUrls.length === 0 && card.dataset.image) {
      imageUrls = [card.dataset.image];
    }

    // Crear slides — un <div> por imagen para que scroll-snap funcione perfecto
    imageUrls.forEach(function (url, i) {
      var slide = document.createElement('div');
      slide.className = 'lj-upsell-detail__slide';
      var img = document.createElement('img');
      img.src = url;
      img.alt = (card.dataset.title || '') + ' - imagen ' + (i + 1);
      img.loading = 'lazy';
      slide.appendChild(img);
      detailSlides.appendChild(slide);
    });

    // Dots de paginacion (solo si hay 2+ imagenes)
    if (detailGalleryDots && imageUrls.length > 1) {
      imageUrls.forEach(function (_, i) {
        var dot = document.createElement('span');
        dot.className = 'lj-upsell-detail__gallery-dot';
        if (i === 0) dot.classList.add('is-active');
        detailGalleryDots.appendChild(dot);
      });
    }

    // Reset scroll a la primera imagen
    detailSlides.scrollTo({ left: 0, behavior: 'instant' });
    bindGalleryScroll();
  }

  // Track del slide visible para actualizar dots al hacer swipe/scroll
  function bindGalleryScroll() {
    if (!detailSlides || !detailGalleryDots) return;
    detailSlides.onscroll = function () {
      var slideWidth = detailSlides.clientWidth;
      var activeIdx = Math.round(detailSlides.scrollLeft / slideWidth);
      var dots = detailGalleryDots.querySelectorAll('.lj-upsell-detail__gallery-dot');
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === activeIdx);
      });
    };
  }

  // ─── DETAIL VIEW ──────────────────────────────────────────────────
  function showDetailFor(index) {
    state.currentIndex = (index + cards.length) % cards.length;
    var card = cards[state.currentIndex];
    if (!card) return;

    // Animación de transición: fade out → swap content → fade in
    detailContent.classList.add('is-swapping');
    setTimeout(function () {
      buildGallery(card);
      detailTitle.textContent = card.dataset.title || '';
      detailDescription.textContent = card.dataset.description || '';
      detailPrice.textContent = card.dataset.priceDisplay || moneyFmt(parseInt(card.dataset.price, 10));

      // Discount badge — si la card tiene compare-at price (mostrado en grid),
      // calculamos el % aqui mismo para evitar tener que pasarlo via data-attr.
      // Tomamos el badge del card si existe (ya tiene el % calculado en Liquid).
      if (detailDiscount && detailDiscountLabel) {
        var cardBadge = card.querySelector('.lj-upsell-card__badge');
        if (cardBadge) {
          detailDiscountLabel.textContent = cardBadge.textContent.trim() + ' Descuento promocional';
          detailDiscount.hidden = false;
        } else {
          detailDiscount.hidden = true;
        }
      }

      // Reset add button — chequea si ya fue agregado antes
      var variantId = card.dataset.variantId;
      var isPack = card.dataset.isPack === 'true';

      // Pack-product en detail view: el CTA del fondo cambia a "🎁 Armar pack"
      // y agrega data-pack-id para que el handler global del pack-builder
      // lo intercepte. Removemos las clases de loading/added porque para packs
      // no aplican (ese flow lo maneja el pack-builder con su propio loading).
      if (isPack) {
        addBtn.classList.remove('is-added', 'is-loading');
        addBtn.setAttribute('data-pack-id', card.dataset.handle);
        addLabel.textContent = '🎁 Armar mi pack';
      } else {
        addBtn.removeAttribute('data-pack-id');
        if (state.addedVariants[variantId]) {
          addBtn.classList.add('is-added');
          addLabel.textContent = 'Quitar de mi compra';
        } else {
          addBtn.classList.remove('is-added');
          addLabel.textContent = 'Añadir a mi compra';
        }
      }

      detailContent.classList.remove('is-swapping');
      buildDots();
      // Reset scroll del content (si la descripcion es larga, queremos empezar arriba)
      detailContent.scrollTo({ top: 0, behavior: 'instant' });
    }, 180);
  }

  function showGridView() {
    state.view = 'grid';
    viewDetail.hidden = true;
    viewGrid.hidden = false;
    panel.dataset.view = 'grid';
  }

  function showDetailView(index) {
    state.view = 'detail';
    viewGrid.hidden = true;
    viewDetail.hidden = false;
    panel.dataset.view = 'detail';
    showDetailFor(index);
  }

  // ─── ADD TO CART (detail view) ───────────────────────────────────
  function addCurrentProduct() {
    if (addBtn.classList.contains('is-loading')) return;
    // Pack-product: el handler global del pack-builder ya intercepto el
    // click via data-pack-id. NO agregar al cart aqui (sino se duplica:
    // se agrega el pack-product entero + se abre el modal pack-builder
    // que despues agrega los items individuales del pack).
    if (addBtn.hasAttribute('data-pack-id')) return;
    var card = cards[state.currentIndex];
    if (!card) return;

    var variantId = card.dataset.variantId;

    // Toggle: si ya esta agregado, quitar (igual que en card view)
    if (state.addedVariants[variantId]) {
      removeCurrentProduct();
      return;
    }

    addBtn.classList.add('is-loading');
    addLabel.textContent = 'Agregando…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['lj-cart-drawer'],
      })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || err.message || 'No se pudo agregar');
          });
        }
        return r.json();
      })
      .then(function (data) {
        var lineKey = data && data.items && data.items[0] && data.items[0].key;
        state.addedVariants[variantId] = lineKey || true;
        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-added');
        addLabel.textContent = 'Quitar de mi compra';
        showToast('Agregado: ' + (card.dataset.title || '').slice(0, 40));
        flyToCart(card);

        // Sincronizar el botón del card en el grid — si el cliente vuelve
        // a la grid view, debe ver "Quitar" (estado is-added) en este card.
        // Faltaba este espejo del que ya existe en removeCurrentProduct()
        // (Bug reportado 2026-05-03: agregado desde detail view dejaba el
        // card del grid mostrando "Agregar").
        var cardAddBtn = card.querySelector('[data-lj-upsell-card-add]');
        if (cardAddBtn) {
          cardAddBtn.classList.add('is-added');
          var cardLabel = cardAddBtn.querySelector('.lj-upsell-card__add-label');
          if (cardLabel) cardLabel.textContent = 'Quitar';
        }

        // Refresh cart drawer HTML si Shopify devolvió la section
        var sectionHtml = data.sections && data.sections['lj-cart-drawer'];
        if (sectionHtml && typeof window.ljCartApplyHtml === 'function') {
          window.ljCartApplyHtml(sectionHtml);
        }
        // Update header pill
        return fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (typeof window.ljCartUpdateHeaderCount === 'function') {
              window.ljCartUpdateHeaderCount(cart);
            }
            updateContinueLabel(cart);
          });
      })
      .catch(function (err) {
        console.error('[lj-upsell] addToCart failed:', err);
        addBtn.classList.remove('is-loading');
        addLabel.textContent = 'Error — intenta de nuevo';
        setTimeout(function () { addLabel.textContent = 'Añadir a mi compra'; }, 2500);
      });
  }

  function removeCurrentProduct() {
    if (addBtn.classList.contains('is-loading')) return;
    var card = cards[state.currentIndex];
    if (!card) return;

    var variantId = card.dataset.variantId;
    var lineKey = state.addedVariants[variantId];
    var idToRemove = (typeof lineKey === 'string') ? lineKey : variantId;

    addBtn.classList.add('is-loading');
    addLabel.textContent = 'Quitando…';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: idToRemove,
        quantity: 0,
        sections: ['lj-cart-drawer'],
      })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || err.message || 'No se pudo quitar');
          });
        }
        return r.json();
      })
      .then(function (data) {
        delete state.addedVariants[variantId];
        addBtn.classList.remove('is-loading');
        addBtn.classList.remove('is-added');
        addLabel.textContent = 'Añadir a mi compra';
        showToast('Quitado: ' + (card.dataset.title || '').slice(0, 40));

        // Tambien actualizar el botoncito del card en el grid
        var cardAddBtn = card.querySelector('[data-lj-upsell-card-add]');
        if (cardAddBtn) {
          cardAddBtn.classList.remove('is-added');
          var cardLabel = cardAddBtn.querySelector('.lj-upsell-card__add-label');
          if (cardLabel) cardLabel.textContent = 'Agregar';
        }

        // Refresh cart drawer + header pill
        var sectionHtml = data && data.sections && data.sections['lj-cart-drawer'];
        if (sectionHtml && typeof window.ljCartApplyHtml === 'function') {
          window.ljCartApplyHtml(sectionHtml);
        }
        return fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (typeof window.ljCartUpdateHeaderCount === 'function') {
              window.ljCartUpdateHeaderCount(cart);
            }
            updateContinueLabel(cart);
          });
      })
      .catch(function (err) {
        console.error('[lj-upsell] removeCurrentProduct failed:', err);
        addBtn.classList.remove('is-loading');
        addLabel.textContent = 'Error — reintenta';
        setTimeout(function () {
          addLabel.textContent = addBtn.classList.contains('is-added') ? 'Quitar de mi compra' : 'Añadir a mi compra';
        }, 2500);
      });
  }

  function updateContinueLabel(cart) {
    if (!continueLabel) return;

    /* Footer dinámico según engagement con el upsell:
       - Si el cliente NO agregó nada durante esta apertura: muestra
         "Continuar al pago" (sin precio) y el botón aparece como link
         subordinado (clase .is-engaged ausente). El estilo débil evita
         que el botón compita con los productos del upsell.
       - Si agregó 1+ productos: muestra "Pagar S/X.XX" con precio
         actualizado, y el botón pasa a estilo CTA naranja prominente
         (clase .is-engaged presente). Es la "recompensa" visual por
         haberse decidido a sumar items.
       Basado en research de Baymard + NN/g sobre jerarquía de botones
       en upsell modals (ver doc 2026-05-03). */
    var engaged = Object.keys(state.addedVariants).length > 0;
    var continueBtn = continueLabel.closest('[data-lj-upsell-continue]')
                   || (modal && modal.querySelector('[data-lj-upsell-continue]'));

    if (engaged && cart && cart.total_price > 0) {
      continueLabel.textContent = 'Pagar ' + moneyFmt(cart.total_price);
      if (continueBtn) continueBtn.classList.add('is-engaged');
    } else {
      continueLabel.textContent = 'Continuar al pago';
      if (continueBtn) continueBtn.classList.remove('is-engaged');
    }
  }

  // ─── ANIMATIONS ──────────────────────────────────────────────────
  function showToast(msg) {
    if (!toast) return;
    if (toastText) toastText.textContent = msg;
    toast.setAttribute('aria-hidden', 'false');
    clearTimeout(toast.__hideTimer);
    toast.__hideTimer = setTimeout(function () {
      toast.setAttribute('aria-hidden', 'true');
    }, 2200);
  }

  function flyToCart(sourceEl) {
    // Crea un clone que vuela hacia el ícono del cart pill en el header.
    // Animación CSS via class — el JS solo coloca el clone y dispara la transición.
    var pill = document.querySelector('.lj-cart-pill');
    if (!pill || !sourceEl) return;
    var img = sourceEl.querySelector('img');
    if (!img) return;

    var sourceRect = img.getBoundingClientRect();
    var targetRect = pill.getBoundingClientRect();
    var clone = img.cloneNode(true);
    clone.className = 'lj-upsell-fly';
    clone.style.position = 'fixed';
    clone.style.left = sourceRect.left + 'px';
    clone.style.top = sourceRect.top + 'px';
    clone.style.width = sourceRect.width + 'px';
    clone.style.height = sourceRect.height + 'px';
    clone.style.zIndex = '10001';
    clone.style.borderRadius = '8px';
    clone.style.pointerEvents = 'none';
    clone.style.transition = 'transform 0.7s cubic-bezier(0.5, 0, 0.5, 1), opacity 0.7s ease, width 0.7s ease, height 0.7s ease';
    document.body.appendChild(clone);

    // Force reflow para que la transición funcione
    void clone.offsetWidth;

    var dx = targetRect.left + targetRect.width / 2 - sourceRect.left - sourceRect.width / 2;
    var dy = targetRect.top + targetRect.height / 2 - sourceRect.top - sourceRect.height / 2;
    clone.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(0.15)';
    clone.style.opacity = '0';
    clone.style.width = '24px';
    clone.style.height = '24px';

    setTimeout(function () { clone.remove(); }, 800);
  }

  // ─── OPEN / CLOSE ─────────────────────────────────────────────────
  function open() {
    state.isOpen = true;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lj-upsell-open');
    showGridView();
    startCountdown();

    // Sincronizar state.addedVariants con el cart REAL — el cliente puede haber
    // removido productos del cart drawer manualmente entre dos aperturas del
    // modal. Sin esta sync, el state interno queda stale y el botón muestra
    // "Quitar" para items que ya no existen en el cart → click → 404 al
    // hacer /cart/change.js con un lineKey inválido.
    // (Bug reportado 2026-05-03.)
    fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncAddedVariantsWithCart(cart);
        updateContinueLabel(cart);
      })
      .catch(function () { /* fallback al label default */ });
  }

  // Sincroniza state.addedVariants y la UI de los cards con el cart real.
  // - Si state tiene un variantId que NO está en cart → removerlo del state +
  //   limpiar UI del card (volver a "Agregar")
  // - Si cart tiene un variantId que SÍ es del upsell pero NO está en state
  //   (cliente lo agregó por otra vía) → agregarlo al state + actualizar UI
  //   del card (mostrar "Quitar"). Esto evita "double add" si vuelve a
  //   clickear el card pensando que no estaba.
  function syncAddedVariantsWithCart(cart) {
    if (!cart || !cart.items) return;
    var cartByVariant = {};
    cart.items.forEach(function (item) {
      cartByVariant[String(item.variant_id)] = item.key || true;
    });

    // Pass 1: limpiar variants que ya no están en cart
    Object.keys(state.addedVariants).forEach(function (variantId) {
      if (!cartByVariant[String(variantId)]) {
        delete state.addedVariants[variantId];
        var card = cards.find(function (c) { return c.dataset.variantId === variantId; });
        if (card) {
          var btn = card.querySelector('[data-lj-upsell-card-add]');
          if (btn) {
            btn.classList.remove('is-added');
            var lbl = btn.querySelector('.lj-upsell-card__add-label');
            if (lbl) lbl.textContent = 'Agregar';
          }
        }
      }
    });

    // Pass 2: marcar como agregados los items del cart que sean del upsell
    cards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      if (!variantId) return;
      var keyInCart = cartByVariant[String(variantId)];
      if (keyInCart && !state.addedVariants[variantId]) {
        state.addedVariants[variantId] = keyInCart;
        var btn = card.querySelector('[data-lj-upsell-card-add]');
        if (btn) {
          btn.classList.add('is-added');
          var lbl = btn.querySelector('.lj-upsell-card__add-label');
          if (lbl) lbl.textContent = 'Quitar';
        }
      } else if (keyInCart && state.addedVariants[variantId] === true) {
        // Refresh stale lineKey: si guardamos `true` (legacy) y ahora tenemos
        // el key real del cart, actualizamos para que removeProduct funcione.
        state.addedVariants[variantId] = keyInCart;
      }
    });
  }

  function close(submitCheckout) {
    state.isOpen = false;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lj-upsell-open');
    stopCountdown();

    if (submitCheckout) {
      /* IMPORTANTE: el cart drawer pudo haberse re-renderizado mientras el
         upsell modal estaba abierto (cuando el cliente agrego productos del
         upsell al cart, el drawer hace `ljCartApplyHtml` con el HTML fresco).
         Eso DESTRUYE el form/btn originales que guardamos en
         `state.pendingCheckoutForm` — quedan zombi (sin parent en DOM).
         Si llamamos `form.requestSubmit()` sobre un nodo zombi, falla
         silenciosamente y el cliente queda atrapado en la PDP.

         Fix: buscar el form/btn FRESH del DOM justo antes de submitear. Asi
         siempre tenemos nodos vivos conectados al document. */
      state.pendingCheckoutForm = null;
      state.pendingCheckoutButton = null;

      var doSubmit = function () {
        setTimeout(function () {
          try {
            var freshBtn = document.querySelector('[data-lj-cart-checkout]') ||
                           document.querySelector('button[name="checkout"]');
            var freshForm = freshBtn && freshBtn.closest('form');
            console.log('[lj-upsell] doSubmit — form:', !!freshForm, 'btn:', !!freshBtn);
            if (!freshForm || !freshBtn) {
              /* No encontramos form en DOM — fallback: ir directo a /checkout via
                 location. Asi al menos el cliente llega al pago. */
              console.warn('[lj-upsell] No form/btn en DOM, redirect directo a /checkout');
              window.location.href = '/checkout';
              return;
            }
            if (freshForm.requestSubmit) {
              freshForm.requestSubmit(freshBtn);
            } else {
              var hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = 'checkout';
              hidden.value = '';
              freshForm.appendChild(hidden);
              freshForm.submit();
            }
          } catch (err) {
            /* CUALQUIER excepcion en el submit (form invalido, requestSubmit
               throw, etc.) → fallback duro a /checkout. El cliente NUNCA
               queda atrapado. */
            console.error('[lj-upsell] submit fallo, fallback a /checkout:', err);
            window.location.href = '/checkout';
          }
        }, 120);
      };

      /* DESHABILITADO-SHALOM (Pedro 2026-05-07): el modal "¿Lima o Provincia?"
         está temporalmente desactivado. Vamos directo al checkout sin paso
         intermedio. Para REACTIVAR: descomenta el if/else original abajo Y
         vuelve a incluir {% section 'lj-shalom-modal' %} en layout/theme.liquid.
         ────────────────────────────────────────────────────────────────────
         if (window.LJ_SHALOM && typeof window.LJ_SHALOM.open === 'function') {
           window.LJ_SHALOM.open(doSubmit);
         } else {
           doSubmit();
         } */
      doSubmit();
    }
  }

  // ─── INTERCEPT CHECKOUT ──────────────────────────────────────────
  function interceptCheckout(e) {
    var btn = e.target.closest('button[name="checkout"]');
    if (!btn) return;

    var form = btn.closest('form');
    if (!form) return;

    e.preventDefault();
    state.pendingCheckoutForm = form;
    state.pendingCheckoutButton = btn;  // guardamos el submitter para requestSubmit

    // Single skip filter: si el cart ya tiene producto Joe Tech, no offer crossell
    cartHasJoeTechProduct().then(function (has) {
      if (has) {
        /* Ya tiene Joe Tech → saltamos el upsell, pero AÚN tenemos que
           pasar por el modal Shalom (paso obligatorio: ¿Lima o Provincia?).
           Si no hay LJ_SHALOM disponible, submit directo como fallback. */
        var doSubmit = function () {
          /* Fresh DOM lookup en este path tambien — el cart drawer puede
             haberse re-renderizado entre el click y el momento de submit
             (por ej. si el cart toast disparo un refresh). */
          try {
            var freshBtn = document.querySelector('[data-lj-cart-checkout]') ||
                           document.querySelector('button[name="checkout"]');
            var freshForm = freshBtn && freshBtn.closest('form');
            console.log('[lj-upsell] skip-path doSubmit — form:', !!freshForm, 'btn:', !!freshBtn);
            if (!freshForm || !freshBtn) {
              window.location.href = '/checkout';
              return;
            }
            if (freshForm.requestSubmit) {
              freshForm.requestSubmit(freshBtn);
            } else {
              var hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = 'checkout';
              hidden.value = '';
              freshForm.appendChild(hidden);
              freshForm.submit();
            }
          } catch (err) {
            console.error('[lj-upsell] skip-path submit fallo, fallback a /checkout:', err);
            window.location.href = '/checkout';
          }
        };
        /* DESHABILITADO-SHALOM (Pedro 2026-05-07) — ver nota arriba.
           Original:
           if (window.LJ_SHALOM && typeof window.LJ_SHALOM.open === 'function') {
             window.LJ_SHALOM.open(doSubmit);
           } else {
             doSubmit();
           } */
        doSubmit();
        return;
      }
      // Mostrar el modal
      open();
    });
  }

  // ─── COUNTDOWN TIMER (10 min envio gratis) ───────────────────────
  // Persiste el deadline en sessionStorage para que si el cliente reabre el
  // modal en la misma sesion, el timer continue desde donde estaba (no reset
  // a 10:00). Si ya expiro, muestra "Expirado" pero el modal sigue funcional.
  var COUNTDOWN_KEY = 'lj_upsell_deadline_v1';
  var COUNTDOWN_DURATION_MS = 10 * 60 * 1000;  // 10 minutos
  var countdownInterval = null;

  function startCountdown() {
    if (!countdownEl || !countdownClock) return;

    // Recuperar deadline existente o crear uno nuevo
    var deadline = parseInt(sessionStorage.getItem(COUNTDOWN_KEY), 10);
    if (!deadline || isNaN(deadline) || deadline < Date.now()) {
      // No hay deadline o ya expiro — crear uno nuevo
      deadline = Date.now() + COUNTDOWN_DURATION_MS;
      sessionStorage.setItem(COUNTDOWN_KEY, String(deadline));
    }

    function tick() {
      var remaining = deadline - Date.now();
      if (remaining <= 0) {
        countdownClock.textContent = '00:00';
        countdownEl.classList.add('is-expired');
        clearInterval(countdownInterval);
        countdownInterval = null;
        return;
      }
      var totalSec = Math.ceil(remaining / 1000);
      var min = Math.floor(totalSec / 60);
      var sec = totalSec % 60;
      countdownClock.textContent =
        (min < 10 ? '0' + min : min) + ':' + (sec < 10 ? '0' + sec : sec);
    }

    tick();
    if (countdownInterval) clearInterval(countdownInterval);
    countdownInterval = setInterval(tick, 1000);
  }

  function stopCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  }

  // ─── ADD / REMOVE TOGGLE FROM CARD ───────────────────────────────
  // El boton "Agregar +" del card es toggle: si el producto NO esta en el
  // cart, click → agrega; si YA esta, click → quita. Esto permite al cliente
  // desagregar facilmente si se equivoco, sin tener que abrir el cart drawer.
  //
  // Para poder quitar especificamente ESTE line item (no otro con el mismo
  // variant pero diferentes properties), guardamos el `line key` del item
  // agregado en state.addedVariants[variantId] = "<key>" (en lugar de true).
  function addProductFromCard(card) {
    var btn = card.querySelector('[data-lj-upsell-card-add]');
    if (!btn || btn.classList.contains('is-loading')) return;

    // Toggle: si ya esta agregado, quitar
    if (btn.classList.contains('is-added')) {
      removeProductFromCard(card);
      return;
    }

    var variantId = card.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    var labelEl = btn.querySelector('.lj-upsell-card__add-label');
    if (labelEl) labelEl.textContent = 'Agregando…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['lj-cart-drawer'],
      })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || err.message || 'No se pudo agregar');
          });
        }
        return r.json();
      })
      .then(function (data) {
        // Guardamos el key del line item para poder removerlo despues
        var lineKey = data && data.items && data.items[0] && data.items[0].key;
        state.addedVariants[variantId] = lineKey || true;
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        if (labelEl) labelEl.textContent = 'Quitar';
        showToast('Agregado: ' + (card.dataset.title || '').slice(0, 40));
        flyToCart(card);

        // Refresh cart drawer + header pill
        var sectionHtml = data.sections && data.sections['lj-cart-drawer'];
        if (sectionHtml && typeof window.ljCartApplyHtml === 'function') {
          window.ljCartApplyHtml(sectionHtml);
        }
        return fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (typeof window.ljCartUpdateHeaderCount === 'function') {
              window.ljCartUpdateHeaderCount(cart);
            }
            updateContinueLabel(cart);
          });
      })
      .catch(function (err) {
        console.error('[lj-upsell] addProductFromCard failed:', err);
        btn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = 'Error — reintenta';
        setTimeout(function () {
          if (labelEl) labelEl.textContent = 'Agregar';
        }, 2500);
      });
  }

  function removeProductFromCard(card) {
    var btn = card.querySelector('[data-lj-upsell-card-add]');
    if (!btn || btn.classList.contains('is-loading')) return;

    var variantId = card.dataset.variantId;
    var lineKey = state.addedVariants[variantId];
    // Si lineKey es solo `true` (legacy o no se capturo), fallback al variantId
    var idToRemove = (typeof lineKey === 'string') ? lineKey : variantId;

    btn.classList.add('is-loading');
    var labelEl = btn.querySelector('.lj-upsell-card__add-label');
    if (labelEl) labelEl.textContent = 'Quitando…';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: idToRemove,
        quantity: 0,
        sections: ['lj-cart-drawer'],
      })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || err.message || 'No se pudo quitar');
          });
        }
        return r.json();
      })
      .then(function (data) {
        delete state.addedVariants[variantId];
        btn.classList.remove('is-loading');
        btn.classList.remove('is-added');
        if (labelEl) labelEl.textContent = 'Agregar';
        showToast('Quitado: ' + (card.dataset.title || '').slice(0, 40));

        // Refresh cart drawer + header pill
        var sectionHtml = data && data.sections && data.sections['lj-cart-drawer'];
        if (sectionHtml && typeof window.ljCartApplyHtml === 'function') {
          window.ljCartApplyHtml(sectionHtml);
        }
        return fetch('/cart.js', { cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (typeof window.ljCartUpdateHeaderCount === 'function') {
              window.ljCartUpdateHeaderCount(cart);
            }
            updateContinueLabel(cart);
          });
      })
      .catch(function (err) {
        console.error('[lj-upsell] removeProductFromCard failed:', err);
        btn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = 'Error — reintenta';
        setTimeout(function () {
          if (labelEl) {
            labelEl.textContent = btn.classList.contains('is-added') ? 'Quitar' : 'Agregar';
          }
        }, 2500);
      });
  }

  // ─── SCROLL LOCK ──────────────────────────────────────────────────
  // En desktop, el `body { overflow: hidden }` no siempre previene el wheel
  // scroll de la pagina detras (el modal panel es position: absolute dentro
  // de .lj-upsell que es position: fixed inset:0). El scroll del documento
  // sigue funcionando si el wheel event burbujea al window.
  //
  // Fix: capturar wheel y touchmove en captura — si el target esta fuera del
  // panel del modal, prevenir default. Asi solo el contenido del modal puede
  // scrollearse mientras esta abierto.
  function preventBodyScroll(e) {
    if (!state.isOpen) return;
    if (panel.contains(e.target)) return;  // dentro del modal — permitir
    e.preventDefault();
  }
  document.addEventListener('wheel', preventBodyScroll, { passive: false });
  document.addEventListener('touchmove', preventBodyScroll, { passive: false });

  // ─── EVENT LISTENERS ──────────────────────────────────────────────
  // Capture phase para interceptar antes que el cart drawer haga su submit
  document.addEventListener('click', interceptCheckout, true);

  // Cards: dos handlers separados
  //   - [data-lj-upsell-card-view]  → abrir detail view
  //   - [data-lj-upsell-card-add]   → agregar directo al cart sin abrir detail
  cards.forEach(function (card) {
    var viewBtn = card.querySelector('[data-lj-upsell-card-view]');
    var addCardBtn = card.querySelector('[data-lj-upsell-card-add]');
    if (viewBtn) {
      viewBtn.addEventListener('click', function () {
        var idx = parseInt(card.dataset.index, 10);
        showDetailView(idx);
      });
    }
    if (addCardBtn) {
      addCardBtn.addEventListener('click', function (e) {
        e.stopPropagation();  // no propagar al card view click
        addProductFromCard(card);
      });
    }
  });

  // Close triggers
  modal.querySelectorAll('[data-lj-upsell-close]').forEach(function (el) {
    el.addEventListener('click', function () { close(false); });
  });

  // Continue al pago
  continueBtn && continueBtn.addEventListener('click', function () {
    close(true);
  });

  // Back to grid
  modal.querySelectorAll('[data-lj-upsell-back]').forEach(function (el) {
    el.addEventListener('click', showGridView);
  });

  // Prev / next
  var prevBtn = modal.querySelector('[data-lj-upsell-prev]');
  var nextBtn = modal.querySelector('[data-lj-upsell-next]');
  prevBtn && prevBtn.addEventListener('click', function () {
    showDetailFor(state.currentIndex - 1);
  });
  nextBtn && nextBtn.addEventListener('click', function () {
    showDetailFor(state.currentIndex + 1);
  });

  // Add to cart
  addBtn && addBtn.addEventListener('click', addCurrentProduct);

  // ESC + arrow keys
  document.addEventListener('keydown', function (e) {
    if (state.isOpen && e.key === 'Escape') close(false);
    if (state.isOpen && state.view === 'detail') {
      if (e.key === 'ArrowLeft') showDetailFor(state.currentIndex - 1);
      if (e.key === 'ArrowRight') showDetailFor(state.currentIndex + 1);
    }
  });

  // Touch swipe para cambiar de PRODUCTO en detail view.
  // IMPORTANTE: si el swipe empieza dentro de la galería, no disparar el cambio
  // de producto — la galería tiene su propio scroll-snap horizontal nativo
  // que ya permite swipear entre IMÁGENES del mismo producto. Sin este check,
  // un swipe sobre la imagen cambiaba imagen Y producto a la vez.
  var touchStartX = 0;
  var touchStartedInGallery = false;
  detailContent && detailContent.addEventListener('touchstart', function (e) {
    touchStartX = e.touches[0].clientX;
    touchStartedInGallery = !!e.target.closest('.lj-upsell-detail__gallery');
  }, { passive: true });
  detailContent && detailContent.addEventListener('touchend', function (e) {
    if (touchStartedInGallery) return;  // swipe en galería → solo cambiar imagen
    var diff = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(diff) < 50) return;
    if (diff < 0) showDetailFor(state.currentIndex + 1);
    else showDetailFor(state.currentIndex - 1);
  }, { passive: true });

  // ─── PUBLIC API (debug) ─────────────────────────────────────────
  window.ljUpsellOpen = open;
  window.ljUpsellClose = function () { close(false); };
  // Limpia el sessionStorage flag legacy si existe (de versiones anteriores).
  // No-op en sesiones nuevas pero útil para clientes con flags viejos.
  window.ljUpsellResetSession = function () {
    sessionStorage.removeItem('lj_upsell_shown_v1');
  };
  // Auto-cleanup del flag legacy al cargar — clientes que abrieron el modal
  // antes del fix tendran el flag set y no veran el modal nunca mas. Limpiarlo
  // garantiza que vean el comportamiento nuevo.
  sessionStorage.removeItem('lj_upsell_shown_v1');
})();
